import React from 'react';
import { AnswersDTO, useRiskToleranceWorkflowQuery } from '../../api/useRiskToleranceWorkflowQuery';
import { Results } from '../../steps/Results/Results';
import { InvestmentObjective } from '../../steps/InvestmentObjective/InvestmentObjective';
import { TimeHorizon } from '../../steps/TimeHorizon/TimeHorizon';
import { HypotheticalPortfolio } from '../../steps/HypotheticalPortfolio/HypotheticalPortfolio';
import { RiskLevel } from '../../steps/RiskLevel/RiskLevel';
import { DownMarket } from '../../steps/DownMarket/DownMarket';
import { IncomeStability } from '../../steps/IncomeStability/IncomeStability';
import { EmergencyHandling } from '../../steps/EmergencyHandling/EmergencyHandling';
import { GliaGlobalStyle } from '../../RiskToleranceWorkflow.styles';
import { OrionRiskToleranceWorkflowLoading } from './OrionRiskToleranceWorkflow.loading';
import { OrionRiskToleranceWorkflowError } from './OrionRiskToleranceWorkflow.error';
import { useOrionRiskToleranceWorkflow } from './useOrionRiskToleranceWorkflow';
import { ScrollHereOnce } from '../../../CommonComponents/ScrollHereOnce';
import { useRouteParams } from '../../hooks/useRouteParams';
import { useResetModuleWrapperStyles } from '../../../_hooks/useResetModuleWrapperStyles';

interface Props {
  isAutoAccountsWorkflow?: boolean;
  onFinish?(): void;
}

export const OrionRiskToleranceWorkflow: React.FC<Props> = ({
  isAutoAccountsWorkflow,
  onFinish,
}) => {
  const { clientId } = useRouteParams();

  const { isLoading, isError, refetch, data } = useRiskToleranceWorkflowQuery({ clientId });

  useResetModuleWrapperStyles();

  if (isLoading) {
    return <OrionRiskToleranceWorkflowLoading />;
  }

  if (isError) {
    return <OrionRiskToleranceWorkflowError onRetry={refetch} />;
  }

  if (data === undefined) {
    return null;
  }

  return (
    <>
      <GliaGlobalStyle />

      <RiskToleranceWorkflowContent
        workflowAnswers={data}
        isAutoAccountsWorkflow={!!isAutoAccountsWorkflow}
        onFinish={onFinish}
      />
    </>
  );
};

export const steps = [
  InvestmentObjective,
  TimeHorizon,
  HypotheticalPortfolio,
  RiskLevel,
  DownMarket,
  IncomeStability,
  EmergencyHandling,
];

interface RiskToleranceWorkflowContentProps {
  workflowAnswers: AnswersDTO | null;
  isAutoAccountsWorkflow: boolean;
  onFinish?(): void;
}

const RiskToleranceWorkflowContent: React.FC<RiskToleranceWorkflowContentProps> = ({
  workflowAnswers,
  isAutoAccountsWorkflow,
  onFinish,
}) => {
  const {
    state: { stepIndex, answers, showResults, setStepIndex, setShowResults, isLoadingNavigateAway },
    actions: { onPrevious, onNext, onSubmit, submitAnswers, completeWorkflow },
    mutations: { submitAnswersMutation, completeWorkflowMutation },
  } = useOrionRiskToleranceWorkflow({
    workflowAnswers,
    isAutoAccountsWorkflow,
    onFinish,
  });

  const {
    data: portfolio,
    isLoading: isSubmittingAnswers,
    isError: isSubmittingAnswersError,
  } = submitAnswersMutation;

  const {
    isLoading: isCompletingWorkflow,
    isError: isCompleteWorkflowError,
    isSuccess: isWorkflowCompleted,
  } = completeWorkflowMutation;

  if (isSubmittingAnswers || isCompletingWorkflow || isWorkflowCompleted || isLoadingNavigateAway) {
    return <OrionRiskToleranceWorkflowLoading />;
  }

  if (isSubmittingAnswersError) {
    return <OrionRiskToleranceWorkflowError onRetry={() => submitAnswers(answers)} />;
  }

  if (isCompleteWorkflowError) {
    return <OrionRiskToleranceWorkflowError onRetry={completeWorkflow} />;
  }

  if (showResults && portfolio) {
    return (
      <Results
        portfolio={portfolio}
        onFinish={completeWorkflow}
        onRestart={() => {
          setShowResults(false);
          setStepIndex(0);
        }}
      />
    );
  }

  const Step = steps[stepIndex];

  return (
    <>
      <ScrollHereOnce key={stepIndex} options={false} />

      <Step
        answers={answers}
        onPrevious={onPrevious}
        onNext={onNext}
        onSubmit={onSubmit}
        submitAnswers={submitAnswers}
        progress={(stepIndex / steps.length) * 100}
      />
    </>
  );
};
